/**
 * `/artists/:artistId` — TASK 8.1 Step 2.
 *
 * The mock's `_artist(V,id)` (L1037) + template (L251-274), wired to
 * `GET /api/artists/:id` (TASK 4.1).
 *
 * ── AC 4.2: NO-YEAR ALBUMS RENDER NO YEAR ─────────────────────────────────
 * `albumCardSchema.year` is `.optional()`, NOT `.nullable()` — dto.ts says in
 * so many words that switching it "would reintroduce exactly the bug the AC
 * forbids". The client half of that contract is {@link albumYearLabel}: it
 * prints a year ONLY for an actual number, so neither `undefined` nor a
 * hypothetical `null` from a future server can ever reach `String()` and paint
 * the words "undefined"/"null" (ui-contract §10.6, `alb-lost` "Lost Sessions").
 * The empty `.sm-cover__year` line is still rendered — the mock's own
 * `min-height:14px` placeholder — so a mixed grid stays aligned.
 *
 * ── ORDERING ──────────────────────────────────────────────────────────────
 * The SERVER sorts (routes/catalog.ts: "year ascending, no-year LAST"), and
 * the client does NOT re-sort. Re-sorting would be wrong, not merely
 * redundant: the order is global across keyset pages, so sorting each arriving
 * page locally would interleave page 2's early years back above page 1's late
 * ones.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 8.1's files */

import {
  ERROR_CODES,
  SAFE_ERROR_MESSAGES,
  TESTIDS,
  actionsForButton,
  albumCardLink,
} from '@streamymusic/shared';

import { availabilityState } from '../../components/AvailabilityBadge.js';
import { VirtualList } from '../../components/VirtualList.js';
import {
  EmptyBlock,
  ErrorBlock,
  GoneBlock,
  LoadingBlock,
  NotFoundBlock,
} from '../../components/StateBlocks.js';
import { Card, FavoriteMarker } from '../../components/primitives/Card.js';
import { append, el } from '../../components/primitives/dom.js';
import { albumArtUrl, gradientFor, initialsOf } from '../../components/primitives/format.js';

/** Cover grids page in 24s (ui-contract §10.6 — the /genres/:id increment). */
export const ALBUM_GRID_PAGE_SIZE = 24;

/**
 * The year line for an album card.
 *
 * @param {{ year?: number }} album
 * @returns {string} '' when the album has no year — NEVER "null"/"undefined"
 */
export function albumYearLabel(album) {
  return typeof album.year === 'number' && Number.isFinite(album.year) ? String(album.year) : '';
}

/**
 * One album tile in any grid.
 *
 * @param {{
 *   album: import('@streamymusic/shared').AlbumCard,
 *   surface?: 'browse'|'search'|'album'|'playlist'|'favorites'|'downloads',
 *   favorite?: boolean,
 *   offline?: boolean,
 *   downloaded?: boolean,
 *   onMenu?: (album: import('@streamymusic/shared').AlbumCard) => void,
 *   tilt?: 'left'|'right',
 *   onContractViolation?: (message: string, detail: Record<string, string>) => void,
 *     reports a SERVER contract breach (a row TASK 4.1/4.2 promised to filter
 *     reached a filter surface) to the app's diagnostics seam. Defaulted to a
 *     console warning inside ../../components/AvailabilityBadge.js only when
 *     no caller injects one — see that module's `defaultScopeViolation`.
 * }} options
 * @returns {HTMLElement}
 */
export function AlbumGridCard(options) {
  const {
    album,
    surface = 'browse',
    favorite = false,
    offline = false,
    downloaded = true,
    onMenu,
    tilt = 'left',
    onContractViolation,
  } = options;

  // ONE decision function for every surface (mock defects 8 + 10). An album
  // that is `available:false` keeps its marker even in browse — the AC 3.9
  // retained-album rule, which is why `kind` is passed explicitly.
  const state = availabilityState({
    available: album.available,
    offline,
    downloaded,
    kind: 'album',
    surface,
    ...(onContractViolation === undefined
      ? {}
      : { onScopeViolation: (message) => onContractViolation(message, { surface, albumId: album.id }) }),
  });

  // Real cover art only when the album has a `covers/…` ref; otherwise the
  // gradient + initials fallback stands (albumArtUrl returns undefined). One
  // rule for every grid — browse, genre, home, favorites — since they all
  // paint through this factory.
  const coverUrl = albumArtUrl(album);

  return Card({
    title: album.title,
    subtitle: album.artistName,
    year: albumYearLabel(album),
    initials: initialsOf(album.title),
    href: `#/albums/${encodeURIComponent(album.id)}`,
    background: gradientFor(album.id),
    ...(coverUrl === undefined ? {} : { coverUrl }),
    ...(state === null ? {} : { status: state, opacity: 0.55 }),
    favorite,
    testId: albumCardLink(album.id),
    tilt,
    ...(onMenu === undefined
      ? {}
      : {
          menu: {
            ariaLabel: `Actions for ${album.title}`,
            testId: actionsForButton(album.title),
            onClick: (event) => {
              event.preventDefault();
              onMenu(album);
            },
          },
        }),
  });
}

/**
 * Builds the `/artists/:id` section and starts loading its first page.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'getArtistAlbums'>,
 *   heading: HTMLElement,
 *   artistId: string,
 *   getState: () => import('../../app/state-store.js').AppState,
 *   subscribe: (listener: (next: import('../../app/state-store.js').AppState) => void) => () => void,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   onAlbumMenu?: (album: import('@streamymusic/shared').AlbumCard) => void,
 *   pageSize?: number,
 *   depth?: import('../../../lib/list-depth.js').ListDepth,
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 *     the app's diagnostics seam (modules/app/index.js). A SERVER contract
 *     violation detected while deciding an availability marker is reported
 *     here rather than only to the user's devtools console.
 * }} options
 *   `getState`/`subscribe` are the store seam AlbumDetail.js already uses:
 *   favourite state is READ AT PAINT TIME and kept live by subscription, so a
 *   ♥ toggled from this page's own ⋯ sheet (or rolled back by a failed write)
 *   repaints the affected card without a navigation.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function ArtistDetail(options) {
  const {
    api,
    heading,
    artistId,
    getState,
    subscribe,
    offline = false,
    downloadedAlbums = [],
    onAlbumMenu,
    pageSize = ALBUM_GRID_PAGE_SIZE,
    depth,
    announce,
    warn,
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Artist detail' } });

  const backLink = append(
    el('p', { className: 'sm-backlink' }),
    el('a', {
      className: 'sm-backlink__a',
      text: '← All artists',
      testId: TESTIDS.ALL_ARTISTS_LINK,
      attrs: { href: '#/browse/artists' },
    }),
  );

  const countLabel = el('p', {
    className: 'sm-count-label sm-count-label--detail',
    attrs: { hidden: true },
  });
  const body = el('div');
  const notice = el('div');
  append(section, backLink, heading, countLabel, body, notice);

  const downloads = new Set(downloadedAlbums);

  /** @type {import('@streamymusic/shared').AlbumCard[]} */
  let items = [];
  /** @type {string|null} */
  let cursor = null;
  let generation = 0;
  let disposed = false;
  /** Cancels the in-flight page fetch on dispose (and on a superseding load). */
  /** @type {AbortController|null} */
  let controller = null;
  /** Pages held, so the depth memory can replay them on return (#back). */
  let pagesLoaded = 0;

  /** The depth this route was left at, read ONCE before the first paint. */
  const restored = depth?.read() ?? null;

  /**
   * Mounted cards, keyed by album id, with the ♥ state they were painted
   * with — the same targeted-repaint mechanism as AlbumDetail's
   * `rowsByTrackId`: held explicitly so a favourite toggle touches ONLY the
   * cards whose state flipped, never the grid.
   *
   * @type {Map<string, { root: HTMLElement, favorited: boolean }>}
   */
  const cardsByAlbumId = new Map();

  const list = VirtualList({
    items,
    windowSize: pageSize,
    renderItem: (album) => {
      const favorited = getState().catalog.favorites.albums.includes(album.id);
      const root = AlbumGridCard({
        album,
        surface: 'browse',
        favorite: favorited,
        offline,
        downloaded: downloads.has(album.id),
        ...(warn === undefined ? {} : { onContractViolation: warn }),
        ...(onAlbumMenu === undefined ? {} : { onMenu: onAlbumMenu }),
      });
      cardsByAlbumId.set(album.id, { root, favorited });
      return root;
    },
    loadMoreLabel: 'Load more ↓',
    // ui-contract §3 enumerates `load-more-button` under /genres/:id, but the
    // id is DERIVED ("kebab-case of the accessible name") and this control is
    // the same §10.6 grid affordance with the same accessible name
    // "Load more ↓" — so the derivation rule, not coincidence, makes the ids
    // equal. The two live on different routes and can never share a DOM; a
    // §3 Tier-1 selector (which navigates to its route first) still resolves
    // exactly one control on either page.
    loadMoreTestId: TESTIDS.LOAD_MORE_BUTTON,
    listClassName: 'sm-grid-covers',
    ...(restored === null ? {} : { initialShown: restored.shown }),
    onWindowChange: (shown) => depth?.remember(pagesLoaded, shown),
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  /** The favourites array the cards were last painted against (identity). */
  let paintedFavAlbums = getState().catalog.favorites.albums;

  /**
   * The C14 targeted repaint: on a store tick, early-out unless the favourite
   * ALBUM set actually changed (the transport patches `pos` ~4×/s — none of
   * those may touch the grid), then toggle the ♥ marker on only the cards
   * whose state flipped.
   */
  function syncFromStore() {
    const favAlbums = getState().catalog.favorites.albums;
    if (favAlbums === paintedFavAlbums) return;
    paintedFavAlbums = favAlbums;
    const favoriteSet = new Set(favAlbums);
    for (const [albumId, entry] of cardsByAlbumId) {
      const favorited = favoriteSet.has(albumId);
      if (favorited === entry.favorited || !entry.root.isConnected) continue;
      entry.favorited = favorited;
      const marker = entry.root.querySelector('.sm-cover__fav');
      if (favorited && marker === null) append(entry.root, FavoriteMarker());
      else if (!favorited && marker !== null) marker.remove();
    }
  }

  const unsubscribe = subscribe(() => {
    if (disposed) return;
    syncFromStore();
  });

  /**
   * @param {{ append?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const own = (generation += 1);
    // A superseded request is CANCELLED, not merely ignored: the generation
    // guard already discards its settle, aborting stops the transfer too.
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage) {
      items = [];
      cursor = null;
      pagesLoaded = 0;
      cardsByAlbumId.clear();
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 4, label: 'Loading albums', variant: 'cover' }));
    }

    try {
      const page = await api.getArtistAlbums(artistId, {
        limit: pageSize,
        ...(appendPage && cursor !== null ? { cursor } : {}),
        signal: ownController.signal,
      });
      if (disposed || own !== generation) return;

      heading.textContent = page.artist.name;
      items = appendPage ? [...items, ...page.items] : [...page.items];
      cursor = page.nextCursor;
      pagesLoaded += 1;

      if (items.length === 0) {
        body.replaceChildren(
          EmptyBlock({
            message: 'No albums for this artist yet.',
            showSyncLink: false,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        return;
      }

      const n = items.length;
      countLabel.textContent = `${n.toLocaleString()}${cursor === null ? '' : '+'} album${n === 1 ? '' : 's'}`;
      countLabel.removeAttribute('hidden');

      paintedFavAlbums = getState().catalog.favorites.albums;
      list.update({ items, hasMore: cursor !== null });
      if (body.firstChild !== list.element) body.replaceChildren(list.element);
      depth?.remember(pagesLoaded, list.shownCount());
    } catch (error) {
      if (disposed || own !== generation) return;
      // Branch on the MACHINE-READABLE code (api-client.ts `ApiError.code`,
      // ERROR_CODES), not the transport status: the status is how the code
      // travelled, the code is what it means.
      const code = /** @type {{ code?: string }} */ (error).code;
      // 404 is NOT retriable (ui-contract §10.6: "path back, no Retry")…
      if (code === ERROR_CODES.NOT_FOUND && !appendPage) {
        heading.textContent = 'Artist';
        body.replaceChildren(
          NotFoundBlock({ message: 'No artist with that id — it may have been removed.' }),
        );
        return;
      }
      // …and neither is 410: "retry never succeeds", so offering one would be
      // a lie. The generic retriable card below is for TRANSIENT failures.
      if (code === ERROR_CODES.GONE && !appendPage) {
        heading.textContent = 'Artist';
        body.replaceChildren(GoneBlock({ message: SAFE_ERROR_MESSAGES[ERROR_CODES.GONE] }));
        return;
      }
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  /**
   * First paint, re-opening the depth the user left (#back). Every replayed
   * page is a hit on the api-client's 24h per-cursor catalog cache, so this
   * costs no network — see `lib/list-depth.ts`.
   *
   * @returns {Promise<void>}
   */
  async function loadToRememberedDepth() {
    await load();
    if (restored === null) return;
    for (let page = 1; page < restored.pages; page += 1) {
      if (disposed || cursor === null) return;
      const before = items.length;
      await load({ append: true });
      if (items.length === before) return; // page failed or added nothing
    }
  }

  void loadToRememberedDepth();

  return {
    element: section,
    dispose() {
      // Cancels every in-flight paint: the next settle sees `disposed` and
      // writes nothing into a section the shell has already replaced. The
      // abort additionally cancels the TRANSFER, and the unsubscribe drops
      // the store listener so it cannot outlive the section.
      disposed = true;
      controller?.abort();
      unsubscribe();
      list.dispose();
    },
  };
}
